import { d as debounce } from './debounce-f0994045.js';
import { E as EVENTS } from './events-58bc9098.js';

// Below this many characters, results are too broad to be useful and every
// keystroke was firing its own live query (each one a real, separate request
// - not just noise from debouncing) - e.g. typing "vitamin" fired a genuine
// query per character, several of which returned identical results once the
// term narrowed enough. The 100ms debounce alone doesn't coalesce normal
// typing speed into fewer requests, so both are needed together.
const MIN_QUERY_LENGTH = 3;
const INPUT_DEBOUNCE_MS = 300;

class SearchInput extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      clearButton: "[js-clear-button]",
      input: "input[type='search'",
    };

    this.classes = {
      inProgress: "in-progress",
    };
  }

  connectedCallback() {
    this.controller = new AbortController();
    this.debounce = debounce();

    this.clearButton = this.querySelector(this.selectors.clearButton);
    this.input = this.querySelector(this.selectors.input);

    this.clearButton.addEventListener(
      "click",
      this.handleClearClicked.bind(this),
      { signal: this.controller.signal },
    );

    this.input.addEventListener("input", () => {
      const length = this.getValue().length;
      // Still dispatch on empty (length 0) so consumers can clear stale
      // results - only 1-2 character queries are suppressed.
      if (length > 0 && length < MIN_QUERY_LENGTH) return;

      this.debounce(() => {
        this.dispatchEvent(
          new CustomEvent(EVENTS.SEARCH_INPUT_CHANGED, {
            bubbles: true,
          }),
        );
      }, INPUT_DEBOUNCE_MS);
    });
  }

  handleClearClicked(event) {
    event.preventDefault();

    this.input.value = "";
    this.input.focus();
    this.input.dispatchEvent(new CustomEvent("input"));
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  getValue() {
    return this.input.value.trim()
  }

  focus() {
    this.input.focus();
  }

  setInProgress(inProgress) {
    if (inProgress) {
      this.classList.add(this.classes.inProgress);
    } else {
      this.classList.remove(this.classes.inProgress);
    }
  }
}

if (!customElements.get("search-input")) {
  customElements.define("search-input", SearchInput);
}